import { injectable } from 'inversify-props';
import { classToPlain, plainToClass } from 'class-transformer';
import { cloneDeep } from 'lodash';
import BaseService from '@/services/base-service';
import UserContactInfo from '@/models/crm/user-contact-info.model';
import EmailModel from '@/models/email.model';
import ContactTypeModel from '@/models/crm/contact-type.model';
import ContactModel from '@/models/crm/contact.model';
import { RequestConfig } from '@/interfaces/request-config.interface';
import { ClientTypeEnum } from '../../enums/client-type.enum';
import WhatsappBlockModel from '@/models/crm/whatsapp-block.model';

@injectable()
export default class ContactService extends BaseService {
  async create(model: ContactModel): Promise<ContactModel> {
    return (await this.httpService.post(
      ContactModel,
      `${this.getApiPath()}/crm/contato`,
      classToPlain(cloneDeep(model)),
    )) as ContactModel;
  }

  async update(model: ContactModel): Promise<ContactModel> {
    return (await this.httpService.put(
      ContactModel,
      `${this.getApiPath()}/crm/contato/${model.id}`,
      classToPlain(cloneDeep(model)),
    )) as ContactModel;
  }

  delete(id: number): Promise<void> {
    return this.httpService.delete(`${this.getApiPath()}/crm/contato/${id}`);
  }

  async getContact(id: number): Promise<ContactModel> {
    return (await this.httpService.get(ContactModel, `${this.getApiPath()}/crm/contato/${id}`)) as ContactModel;
  }

  async getContacts(clientId: string, type: ClientTypeEnum, allowInactiveClients = false): Promise<ContactModel[]> {
    const config: RequestConfig = {
      params: {
        clientId: clientId.replace(/\D/g, ''),
        type,
        allowInactiveClients,
      },
    };

    const result = (await this.httpService.get(
      ContactModel,
      `${this.getApiPath()}/crm/contato/list`,
      config,
    )) as ContactModel[];

    console.log(result);

    return result;
  }

  async getContactTypes(): Promise<ContactTypeModel[]> {
    return (await this.httpService.get(
      ContactTypeModel,
      `${this.getApiPath()}/crm/tipo-contato/list`,
    )) as ContactTypeModel[];
  }

  async getLoggedUserContactInfo(): Promise<UserContactInfo> {
    return (await this.httpService.get(
      UserContactInfo,
      `${this.getApiPath()}/crm/contato/user-contact-info`,
    )) as UserContactInfo;
  }

  async sendEmail(model: EmailModel): Promise<EmailModel> {
    const modelAux = plainToClass(EmailModel, {
      cnpj: model.cnpj,
      prospect: model.prospect,
      from: model.from,
      to: model.to,
      cc: model.cc,
      subject: model.subject,
      sendCopy: model.sendCopy,
      message: model.message,
      attachments: model.attachments,
      groupArea: model.groupArea,
      historyType: model.historyType,
    });

    return (await this.httpService.post(
      EmailModel,
      `${this.getApiPath()}/crm/contato/send-email`,
      classToPlain(cloneDeep(modelAux)),
    )) as EmailModel;
  }

  async getContactByClientId(
    contactNumber: string,
    clientId: string,
    type: ClientTypeEnum,
    allowInactiveClients = false,
  ): Promise<ContactModel | undefined> {
    const contacts = await this.getContacts(clientId, type, allowInactiveClients);
    const contact = contacts.find((item) => {
      if (item.whatsapp && ContactService.checkNumberWhats(item.whatsapp, contactNumber)) {
        return true;
      }
      if (item.telefone && ContactService.checkNumber(item.telefone, contactNumber)) {
        return true;
      }

      return false;
    });

    return contact;
  }

  async verifyWaIdContacts(contactId: number | null,
    contactNumber: string,
    clientType: ClientTypeEnum | null,
    clientId: string | null,
    isSaving = true,
    verifyPhone = false): Promise<Array<ContactModel>> {
    const config: RequestConfig = {
      params: {
        isSaving,
        contactId,
        phoneNumber: contactNumber,
        clientType,
        clientId,
        verifyPhone,
      },
    };
    return (await this.httpService.get(
      ContactModel,
      `${this.getApiPath()}/crm/contato/verify-contacts`,
      config,
    )) as Array<ContactModel>;
  }

  async verifyWaIdContactsProspect(clientId: number | undefined,
    contactNumber: string, isSaving = true, verifyPhone = false): Promise<Array<ContactModel>> {
    const config: RequestConfig = {
      params: {
        isSaving,
        clientId,
        phoneNumber: contactNumber,
        verifyPhone,
      },
    };
    return (await this.httpService.get(
      ContactModel,
      `${this.getApiPath()}/crm/contato/verify-contacts-prospect`,
      config,
    )) as Array<ContactModel>;
  }

  async verifyWaIdContactsClient(clientId: number | undefined,
    contactNumber: string, isSaving = true, verifyPhone = false): Promise<Array<ContactModel>> {
    const config: RequestConfig = {
      params: {
        isSaving,
        clientId,
        phoneNumber: contactNumber,
        verifyPhone,
      },
    };
    return (await this.httpService.get(
      ContactModel,
      `${this.getApiPath()}/crm/contato/verify-contacts-client`,
      config,
    )) as Array<ContactModel>;
  }

  static checkNumber(contactNumber: string, targetNumber: string): boolean {
    const parsedTargetNumber = targetNumber.replace(/\D/g, '');
    const parsedContactNumber = contactNumber.replace(/\D/g, '');

    let alternativeContactNumber = '';

    if (parsedContactNumber.length > 10) {
      alternativeContactNumber = `${parsedContactNumber.substring(0, 2)}${parsedContactNumber.slice(3)}`;
    } else {
      alternativeContactNumber = `${parsedContactNumber.substring(0, 2)}9${parsedContactNumber.substring(2)}`;
    }

    if (parsedContactNumber === parsedTargetNumber || alternativeContactNumber === parsedTargetNumber) {
      return true;
    }

    return false;
  }

  async blockNumber(model: WhatsappBlockModel): Promise<WhatsappBlockModel> {
    const modelAux = plainToClass(WhatsappBlockModel, {
      numeroWhatsapp: model.numeroWhatsapp,
      idContato: model.idContato,
      motivo: model.motivo,
      status: 1,
    });

    return (await this.httpService.post(
      WhatsappBlockModel,
      `${this.getApiPath()}/crm/bloqueio-whatsapp/block`,
      classToPlain(cloneDeep(modelAux)),
    )) as WhatsappBlockModel;
  }

  async unblockNumber(model: WhatsappBlockModel): Promise<string> {
    return (await this.httpService.post(
      String,
      `${this.getApiPath()}/crm/bloqueio-whatsapp/unblock?
      numeroWhatsapp=${model.numeroWhatsapp}`,
    )) as string;
  }

  async getBlockedNumbers(): Promise<Array<string>> {
    return (await this.httpService.get(
      String,
      `${this.getApiPath()}/crm/bloqueio-whatsapp/list`,
    )) as Array<string>;
  }

  async sendToErp(model: ContactModel): Promise<ContactModel> {
    const modelAux = model;
    if (!model.tiposContato) modelAux.tiposContato = [];

    return (await this.httpService.post(
      ContactModel,
      `${this.getApiPath()}/crm/contato/${model.id}/send-to-erp`,
      classToPlain(cloneDeep(model)),
    )) as ContactModel;
  }

  static checkNumberWhats(contactNumber: string, targetNumber: string): boolean {
    const parsedTargetNumber = targetNumber.replace(/\D/g, '');
    const parsedContactNumber = contactNumber.replace(/\D/g, '');

    let alternativeContactNumber = '';

    if (parsedContactNumber.startsWith('55')) {
      if (parsedContactNumber.length > 10) {
        alternativeContactNumber = `${parsedContactNumber.substring(0, 2)}${parsedContactNumber.slice(3)}`;
      } else {
        alternativeContactNumber = `${parsedContactNumber.substring(0, 2)}9${parsedContactNumber.substring(2)}`;
      }
    } else {
      alternativeContactNumber = parsedContactNumber;
    }

    if (parsedContactNumber === parsedTargetNumber || alternativeContactNumber === parsedTargetNumber) {
      return true;
    }

    return false;
  }
}
